import { useRef, useState } from 'react';
import { Upload, Check, AlertTriangle } from '@/components/icons';
import type { ImportResult } from '@/api/items';
import { apiError } from '@/lib/apiError';
import { Button } from './Button';

/** A described template column — shown as a row in the details table. */
export interface ImportColumn {
  name: string;
  /** What the uploader should put in this column. */
  hint: string;
  /** A sample value. */
  example?: string;
  /** Marked as optional in the details table (default: required). */
  optional?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Column headers expected in the Excel file (rendered as chips). */
  expectedColumns: string[];
  /** When provided, a details table (Column · What to enter · Example) is shown
   *  instead of the plain chip list. */
  columnDetails?: ImportColumn[];
  /** Template file download path or null. `progress` shows a line while a long import saves. */
  onImport: (file: File, progress: (message: string) => void) => Promise<ImportResult>;
  onSuccess: () => void;
}

export function ExcelImportModal({ open, onClose, title, expectedColumns, columnDetails, onImport, onSuccess }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');

  if (!open) return null;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setError('');
  }

  async function handleImport() {
    if (!file) { setError('Please select an Excel file (.xlsx)'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await onImport(file, setProgress);
      setResult(res);
      if (res.imported > 0) onSuccess();
    } catch (e: unknown) {
      // The user backed out of a confirm step (e.g. SIS oversold lines) — say so plainly.
      if (e instanceof Error && e.name === 'UserCancelled') { setError(e.message); return; }
      // Show a clean, user-facing reason — never the raw HTTP status,
      // axios message, or server/DB internals.
      setError(apiError(e, 'Import failed. Please check the file and try again.'));
    } finally {
      setLoading(false);
      setProgress('');
    }
  }

  function handleClose() {
    setFile(null);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-2xl rounded-xl border border-[var(--border-color)] shadow-2xl"
        style={{ background: 'var(--bg-primary)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-4">
          <h2 className="inline-flex items-center gap-1.5 text-base font-bold text-[var(--text-primary)]"><Upload size={16} /> Import {title}</h2>
          <button onClick={handleClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Instructions */}
          <div className="rounded-lg border border-[var(--border-color)] p-3 text-sm text-[var(--text-secondary)] space-y-1" style={{ background: 'var(--bg-secondary)' }}>
            <div className="font-semibold text-[var(--text-primary)] mb-1">Excel Column Headers (Row 1):</div>
            {columnDetails && columnDetails.length > 0 ? (
              <div className="max-h-64 overflow-y-auto overflow-x-auto rounded border border-[var(--border-color)]">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0" style={{ background: 'var(--bg-primary)' }}>
                    <tr className="text-[var(--text-muted)]">
                      <th className="px-2 py-1.5 font-semibold">Column</th>
                      <th className="px-2 py-1.5 font-semibold">What to enter</th>
                      <th className="px-2 py-1.5 font-semibold">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columnDetails.map((c) => (
                      <tr key={c.name} className="border-t border-[var(--border-color)] align-top">
                        <td className="whitespace-nowrap px-2 py-1.5">
                          <span className="font-mono text-accent">{c.name}</span>
                          {c.optional && <span className="ml-1 text-[10px] text-[var(--text-muted)]">(optional)</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[var(--text-secondary)]">{c.hint}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[var(--text-muted)]">{c.example ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {expectedColumns.map(col => (
                  <span key={col} className="rounded bg-accent/10 px-2 py-0.5 text-xs font-mono text-accent border border-accent/20">{col}</span>
                ))}
              </div>
            )}
            <div className="text-xs text-[var(--text-muted)] mt-2">
              Column headers must match exactly (case-insensitive). Each data row is imported independently — errors in one row won't affect others.
            </div>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-1">Select Excel File (.xlsx)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFile}
              className="block w-full text-sm text-[var(--text-secondary)] file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-[var(--border-color)] file:bg-white/5 file:text-sm file:font-semibold file:text-[var(--text-primary)] file:cursor-pointer hover:file:bg-white/10"
            />
            {file && (
              <div className="mt-1 text-xs text-[var(--text-muted)]">
                {file.name} — {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {/* Progress of a long import */}
          {loading && progress && (
            <div className="rounded-lg border border-[var(--border-color)] px-3 py-2 text-sm text-[var(--text-secondary)]" role="status">{progress}</div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">{error}</div>
          )}

          {/* Result */}
          {result && (
            <div className="rounded-lg border border-[var(--border-color)] p-3 space-y-2" style={{ background: 'var(--bg-secondary)' }}>
              <div className="flex gap-4 text-sm font-semibold">
                <span className="text-green-400"><Check size={12} className="mr-1 inline-block align-[-2px]" /> Imported: {result.imported}</span>
                {result.skipped > 0 && <span className="text-yellow-400"><AlertTriangle size={12} className="mr-1 inline-block align-[-2px]" /> Skipped: {result.skipped}</span>}
              </div>
              {result.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  <div className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Row Errors:</div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-xs text-red-400 flex gap-2">
                      <span className="text-[var(--text-muted)] flex-shrink-0">Row {e.row}:</span>
                      <span>{e.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-3">
          <Button variant="glass" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" onClick={handleImport} disabled={!file || loading}>
            {loading ? 'Importing…' : 'Import'}
          </Button>
        </div>
      </div>
    </div>
  );
}
